import { ControlledInput } from 'Core/ControlledInput'
import { BlockComponentFactory } from 'shared/express-interface/blocks-core/BlockFactory'
import { isEmpty } from 'lodash'
import { EditorType } from 'Types/Tasks.types'
import { MarkdownEditor } from 'Components/common/MarkdownEdtor/MarkdownEditor'
import { ATTRIBUTE_TYPES } from 'Constants/attributes.constants'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useBlockRef, useBlockState } from 'shared/express-interface/blocks-core/BlockHooks'
import { BlockStateFormatKey } from 'shared/express-interface/blocks-core/ITaskBlock'
import { RichTextEditor } from 'Components/common/RichTextEditor/RichTextEditor'
import { useTaskContext } from 'Contexts/TaskContext'

export type InputEditorProps = {
    onUpdateContent: (content: string) => void
    getValue: () => string
    editorType?: EditorType
    editorConfig?: {
        markdownEditorConfig?: {
            height?: string
        }
    }
}

export const InputEditorBlockComponent =
    BlockComponentFactory.createBlockComponent<InputEditorProps>(props => {
        const taskContext = useTaskContext()
        const { blockId } = props
        const editorType =
            props.editorType ??
            taskContext.studio.taskConfig.assignment.task.config.editor_config?.type
        const [answer, setAnswer] = useBlockState(
            'answer',
            props.getValue(),
            editorType === EditorType.MarkdownEditor
                ? BlockStateFormatKey.Markdown
                : BlockStateFormatKey.Html
        )
        const [isEditorReady, setIsEditorReady] = useState(false)
        const isPasteFunctionalityEnabled =
            taskContext.studio.taskConfig.assignment.task.config.paste_functionality_config
                ?.is_enabled ?? false
        const markdownEditorRef = useRef<{ getContainer: () => HTMLTextAreaElement | null }>(null)
        const richTextEditorRef = useRef<{ getContainer: () => HTMLIFrameElement | null }>(null)

        useBlockRef<{ getContainer: () => HTMLElement | null }>({
            getContainer: () => {
                if (editorType === EditorType.MarkdownEditor) {
                    return markdownEditorRef.current?.getContainer() as HTMLElement
                } else {
                    return richTextEditorRef.current?.getContainer() as HTMLElement
                }
            },
        })

        useEffect(() => {
            setAnswer(answer.value ?? '')
            props.onUpdateContent(answer.value ?? '')
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [isEditorReady])

        const editor = useMemo(() => {
            if (editorType === EditorType.MarkdownEditor) {
                return (
                    <MarkdownEditor
                        type={ATTRIBUTE_TYPES.MARKDOWN_EDITOR}
                        label="Summary"
                        onChange={(content?: string) => {
                            setAnswer(content ?? '')
                            props.onUpdateContent(content ?? '')
                        }}
                        readonly={taskContext.isReadOnly()}
                        id={blockId}
                        value={answer.value}
                        isPasteFunctionalityEnabled={isPasteFunctionalityEnabled}
                        ref={markdownEditorRef}
                        height={props.editorConfig?.markdownEditorConfig?.height}
                    />
                )
            } else {
                return (
                    <RichTextEditor
                        label="Summary"
                        onChange={(content?: string) => {
                            setAnswer(content ?? '')
                            props.onUpdateContent(content ?? '')
                        }}
                        readonly={taskContext.isReadOnly()}
                        id={blockId}
                        value={answer.value}
                        installedPlugins={
                            taskContext.studio.pluginManager.getInstalledPlugins() ?? []
                        }
                        keyBindingActions={taskContext.keyBindingActions}
                        isPasteFunctionalityEnabled={isPasteFunctionalityEnabled}
                        ref={richTextEditorRef}
                        onEditorReady={() => setIsEditorReady(true)}
                    />
                )
            }
        }, [
            editorType,
            taskContext,
            answer.value,
            isPasteFunctionalityEnabled,
            setAnswer,
            props,
            blockId,
        ])

        return (
            <ControlledInput
                id={blockId}
                type={isEmpty(editorType) ? EditorType.RichText : editorType!}
                allowedPlugins={[
                    'Grammar Check',
                    'Readability',
                    'Sentiment Analysis',
                    'Plagiarism',
                    'AI Assistance',
                    'AI Detection',
                    'Soft Rules',
                    'Summarisation Quality',
                    'Live Feedback',
                ]}
                rules={[
                    {
                        type: 'required',
                        message: 'Summary is required',
                    },
                ]}
            >
                {editor}
            </ControlledInput>
        )
    })
